import {
  memo,
  useCallback,
  ChangeEvent,
  FormEvent,
  useState,
  useMemo,
  useRef,
  useEffect
} from 'react';
import Button from 'components/base/Button';
import DatePicker from 'components/base/DatePicker';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { jobBreadcrumbItems } from 'data/commonData';
import { Employee, JobClient, ContactPerson } from 'data/users';
import { Col, Form, Row, Spinner, Modal } from 'react-bootstrap';
import { useQueries, useMutation, useQueryClient } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import { GET_CUSTOMERS_QUERY, GET_COMPANY_REPS } from 'services/api/customer';
import { GET_EMPLOYEES_QUERY } from 'services/api/employee';
import { CREATE_JOB } from 'services/api/job';
import { GET_JOBSTAGE_QUERY, JobStage } from 'services/api/jobStage';
import { JobType, SEARCH_JOB_TYPES } from 'services/api/jobTypes';
import { Location } from 'services/api/location';
import { RigResponse, SEARCH_RIGS } from 'services/api/rigs';
import toast, { Renderable, Toast, ValueFunction } from 'react-hot-toast';
import { toSentenceCase } from 'helpers/utils';
import PhoenixLoader from 'components/common/PhoenixLoader';
import { debounce } from 'lodash';
import { SEARCH_CUSTOMERS } from 'services/api/customer-rep';
import CreateCustomer from '../customers/create-customer';
import {
  QueryResponse,
  FormData,
  IndexKey,
  REQUIRED_FIELDS,
  ObjectField,
  stageOrder,
  DestinationType
} from './types/types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlus } from '@fortawesome/free-solid-svg-icons';
import RigModal from 'components/modals/RigModal';
import LocationModal from 'components/modals/LocationModal';
import { SEARCH_LOCATIONS } from 'services/api/location';
import RequiredLabel, { isValidObjectId } from 'components/labels/Label';

const CreateNewProject: React.FC = () => {
  const [validated, setValidated] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormData>({
    title: '',
    well: '',
    destinationType: '' as DestinationType,
    departureDate: undefined,
    returnDate: undefined,
    expectedEquipmentShipDate: undefined,
    leavingFrom: '',
    comment: '',
    description: '',
    supervisor: [],
    rig: { _id: '' },
    jobType: { _id: '' },
    location: { _id: '' },
    jobStage: { _id: '' },
    customer: { _id: '' },
    customerRepresentative: { _id: '' },
    jobNumber: ''
  });
  const [validationMessages, setValidationMessages] = useState<
    Record<string, string>
  >({});
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchResults, setSearchResults] = useState<JobClient[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [selectedCustomer, setSelectedCustomer] = useState<JobClient | null>(
    null
  );
  const [customerReps, setCustomerReps] = useState<ContactPerson[]>([]);
  const [isLoadingCustomerReps, setIsLoadingCustomerReps] =
    useState<boolean>(false);
  const [rigSearchResults, setRigSearchResults] = useState<RigResponse[]>([]);
  const [isRigSearching, setIsRigSearching] = useState<boolean>(false);
  const [rigSearchTerm, setRigSearchTerm] = useState<string>('');
  const [showRigModal, setShowRigModal] = useState<boolean>(false);
  const [jobTypeSearchResults, setJobTypeSearchResults] = useState<JobType[]>(
    []
  );
  const [isJobTypeSearching, setIsJobTypeSearching] = useState<boolean>(false);
  const [jobTypeSearchTerm, setJobTypeSearchTerm] = useState<string>('');
  const [locationSearchResults, setLocationSearchResults] = useState<
    Location[]
  >([]);
  const [isLocationSearching, setIsLocationSearching] =
    useState<boolean>(false);
  const [locationSearchTerm, setLocationSearchTerm] = useState<string>('');
  const [showLocationModal, setShowLocationModal] = useState<boolean>(false);
  const [isRigInputFocused, setIsRigInputFocused] = useState<boolean>(false);
  const [isJobTypeInputFocused, setIsJobTypeInputFocused] =
    useState<boolean>(false);
  const [isLocationInputFocused, setIsLocationInputFocused] =
    useState<boolean>(false);
  const [isCustomerInputFocused, setIsCustomerInputFocused] =
    useState<boolean>(false);
  const customerRef = useRef<HTMLDivElement>(null);
  const rigRef = useRef<HTMLDivElement>(null);
  const jobTypeRef = useRef<HTMLDivElement>(null);
  const locationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      const isDatePickerClick =
        target.closest('.flatpickr-calendar') ||
        target.closest('.date-picker-container') ||
        target.closest('.flatpickr-input');
      if (isDatePickerClick) {
        return;
      }

      const isDropdownClick =
        target.closest('.form-floating') ||
        target.closest('.dropdown-container');
      if (!isDropdownClick) {
        setIsCustomerInputFocused(false);
        setSearchResults([]);
        if (!(formData.customer as any).id && !formData.customer._id) {
          setSearchTerm('');
        }

        setIsRigInputFocused(false);
        setRigSearchResults([]);
        if (!(formData.rig as any).id && !formData.rig._id) {
          setRigSearchTerm('');
        }

        setIsJobTypeInputFocused(false);
        setJobTypeSearchResults([]);
        if (!(formData.jobType as any).id && !formData.jobType._id) {
          setJobTypeSearchTerm('');
        }

        setIsLocationInputFocused(false);
        setLocationSearchResults([]);
        if (!(formData.location as any).id && !formData.location._id) {
          setLocationSearchTerm('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [formData]);

  useEffect(() => {
    if (!formData.customer._id) {
      setCustomerReps([]);
    }
  }, [formData.customer._id]);

  const queries = useQueries([
    { queryKey: 'customers', queryFn: GET_CUSTOMERS_QUERY },
    { queryKey: 'employees', queryFn: GET_EMPLOYEES_QUERY },
    { queryKey: 'jobStages', queryFn: GET_JOBSTAGE_QUERY }
  ]);

  const [customers, employees, jobStages] = queries.map(
    q => q.data as QueryResponse<any>
  );

  const isLoading = queries.some(q => q.isLoading);

  const createJobMutation = useMutation('createJobMutation', CREATE_JOB, {
    onSuccess: () => {
      queryClient.invalidateQueries('getJobs');
      toast.success('New Job created successfully');
      navigate('/home');
    },
    onError: (error: any): any => {
      if (error?.response?.data.message) {
        return toast.error(error?.response?.data.message);
      } else {
        error?.response?.data.messages.map(
          (message: Renderable | ValueFunction<Renderable, Toast>) =>
            toast.error(toSentenceCase(message as string))
        );
      }
    }
  });

  const validateForm = useCallback(() => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};
    let isValid = true;

    REQUIRED_FIELDS.forEach(field => {
      const value = formData[field as keyof FormData];

      if (ObjectField.includes(field)) {
        if (field === 'supervisor') {
          const supervisors = value as IndexKey[];
          if (!supervisors?.[0]?._id || !isValidObjectId(supervisors[0]._id)) {
            isValid = false;
            newErrors[field] = `${
              field.charAt(0).toUpperCase() + field.slice(1)
            } is required`;
          }
        } else {
          const indexKeyValue = value as IndexKey;
          if (!indexKeyValue?._id || !isValidObjectId(indexKeyValue._id)) {
            isValid = false;
            newErrors[field as keyof FormData] = `${
              field.charAt(0).toUpperCase() + field.slice(1)
            } is required`;
          }
        }
      } else {
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          isValid = false;
          newErrors[field as keyof FormData] = `${
            field.charAt(0).toUpperCase() + field.slice(1)
          } is required`;
        }
      }
    });

    if (formData.jobType._id === '') {
      isValid = false;
      newErrors.jobType = 'Job Type is required';
    }

    setValidationMessages(newErrors);
    setValidated(true);
    return isValid;
  }, [formData]);

  const handleInputChange = useCallback(
    (
      e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
      const { name, value } = e.target;

      setFormData(prevData => {
        if (!ObjectField.includes(name)) {
          return { ...prevData, [name]: value };
        }

        const fieldName = name as keyof FormData;
        const newData = { ...prevData };

        if (fieldName === 'supervisor') {
          newData[fieldName] = [{ _id: value }];
        } else {
          if (fieldName === 'customer') {
            const customerData = customers?.data?.find(
              (c: JobClient) => c._id === value
            );
            if (customerData) {
              newData.title = customerData.organizationName;
            }
          }
          (newData[fieldName] as IndexKey) = { _id: value };
        }

        return newData;
      });

      setValidationMessages(prev => ({ ...prev, [name]: '' }));
    },
    [customers?.data]
  );

  const handleCreateJob = async (e: FormEvent) => {
    e.preventDefault();
    setValidated(true);

    if (!validateForm()) {
      return;
    }

    await createJobMutation.mutateAsync(formData);
  };

  const handleDateChange = useCallback((value: Date, name: string) => {
    setFormData(prev => ({ ...prev, [name]: new Date(value) }));
    setValidationMessages(prev => ({ ...prev, [name]: '' }));
  }, []);

  const sortedJobStages = useMemo(() => {
    if (!jobStages?.data) return [];

    const stages = jobStages.data;

    const newJobStage = stages.find(
      (stage: JobStage) => stage.name.toLowerCase() === 'new job'
    );

    if (newJobStage && !formData.jobStage._id) {
      setFormData(prev => ({
        ...prev,
        jobStage: { _id: newJobStage.id }
      }));
    }
    return stages.sort((a: JobStage, b: JobStage) => {
      const indexA = stageOrder.indexOf(a.name.trim());
      const indexB = stageOrder.indexOf(b.name.trim());
      return indexA - indexB;
    });
  }, [jobStages?.data]);

  const debouncedSearch = useCallback(
    debounce(async (query: string) => {
      if (query.length < 2) return;
      setIsSearching(true);
      try {
        const response = await SEARCH_CUSTOMERS(query);
        setSearchResults(response.data);
      } catch (error) {
        console.error('Error searching customers:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 500),
    []
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.length >= 2) {
      debouncedSearch(value);
    } else {
      setSearchResults([]);
      // Clear customer reps if no customer is selected
      if (!selectedCustomer?._id) {
        setCustomerReps([]);
      }
    }
  };

  const handleShowModal = () => setShowCreateModal(true);
  const handleCloseModal = () => setShowCreateModal(false);

  const handleCustomerCreated = () => {
    handleCloseModal();
    setSearchResults([]);
    if (searchTerm.length >= 2) {
      debouncedSearch(searchTerm);
    }
  };

  const debouncedRigSearch = useCallback(
    debounce(async (query: string) => {
      if (query.length < 2) return;
      setIsRigSearching(true);
      try {
        const { data } = await SEARCH_RIGS(query);
        setRigSearchResults(data);
      } catch (error) {
        console.error('Error searching rigs:', error);
        setRigSearchResults([]);
      } finally {
        setIsRigSearching(false);
      }
    }, 500),
    []
  );

  const handleRigSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setRigSearchTerm(value);
    if (value.length >= 2) {
      debouncedRigSearch(value);
    } else {
      setRigSearchResults([]);
    }
  };

  const handleShowRigModal = () => setShowRigModal(true);
  const handleCloseRigModal = () => setShowRigModal(false);

  const handleRigCreated = () => {
    handleCloseRigModal();
    setRigSearchResults([]);
    if (rigSearchTerm.length >= 2) {
      debouncedRigSearch(rigSearchTerm);
    }
  };

  const debouncedJobTypeSearch = useCallback(
    debounce(async (query: string) => {
      if (query.length < 2) return;
      setIsJobTypeSearching(true);
      try {
        const response = await SEARCH_JOB_TYPES(query);
        setJobTypeSearchResults(response.data);
      } catch (error) {
        console.error('Error searching job types:', error);
        setJobTypeSearchResults([]);
      } finally {
        setIsJobTypeSearching(false);
      }
    }, 500),
    []
  );

  const handleJobTypeSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setJobTypeSearchTerm(value);
    if (value.length >= 2) {
      debouncedJobTypeSearch(value);
    } else {
      setJobTypeSearchResults([]);
    }
  };

  const handleShowLocationModal = () => setShowLocationModal(true);
  const handleCloseLocationModal = () => setShowLocationModal(false);

  const handleLocationCreated = () => {
    handleCloseLocationModal();
    setLocationSearchResults([]);
    if (locationSearchTerm.length >= 2) {
      debouncedLocationSearch(locationSearchTerm);
    }
  };

  const debouncedLocationSearch = useCallback(
    debounce(async (query: string) => {
      if (query.length < 2) return;
      setIsLocationSearching(true);
      try {
        const { data } = await SEARCH_LOCATIONS(query);
        setLocationSearchResults(data);
      } catch (error) {
        console.error('Error searching locations:', error);
        setLocationSearchResults([]);
      } finally {
        setIsLocationSearching(false);
      }
    }, 500),
    []
  );

  const handleLocationSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocationSearchTerm(value);
    if (value.length >= 2) {
      debouncedLocationSearch(value);
    } else {
      setLocationSearchResults([]);
    }
  };

  if (isLoading) return <PhoenixLoader />;
  return (
    <div>
      <PageBreadcrumb items={jobBreadcrumbItems} />
      <h2 className="mb-4">Create a Job</h2>
      <Row>
        <Col xs={12} xl={12}>
          <Form
            noValidate
            validated={validated}
            onSubmit={handleCreateJob}
            className="mb-0"
          >
            <Row className="g-2">
              <Col sm={6} md={8} className="">
                <Form.Group controlId="floatingInputGrid">
                  <Form.Label className="mb-2">Job title</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Job title"
                    onChange={handleInputChange}
                    value={formData.title}
                    required
                    name="title"
                    disabled={true}
                    isInvalid={!!validationMessages.title}
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationMessages.title || 'Please enter a title.'}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col sm={6} md={4}>
                <Form.Group controlId="floatingInputWell">
                  <Form.Label>
                    <RequiredLabel>Well</RequiredLabel>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Well Name"
                    name="well"
                    value={formData.well}
                    onChange={handleInputChange}
                    isInvalid={validated && !formData.well}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationMessages.well || 'Please enter a well.'}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col sm={6} md={8}>
                <Form.Group controlId="floatingSelectTeam">
                  <Form.Label>
                    <RequiredLabel>Destination Type</RequiredLabel>
                  </Form.Label>
                  <Form.Select
                    name="destinationType"
                    value={formData.destinationType}
                    onChange={handleInputChange}
                    isInvalid={validated && !formData.destinationType}
                    style={
                      validated && !formData.destinationType
                        ? { border: '1px solid red' }
                        : {}
                    }
                    required
                  >
                    <option>Select Destination Type</option>
                    <option value="Domestic">Domestic</option>
                    <option value="International">International</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {validationMessages.destinationType ||
                      'Please select a destination type.'}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col sm={6} md={4}>
                <Form.Group controlId="floatingSelectAdmin">
                  <Form.Label>
                    <RequiredLabel>Job Lead</RequiredLabel>
                  </Form.Label>
                  <Form.Select
                    name="supervisor"
                    value={formData?.supervisor?.[0]?._id}
                    onChange={handleInputChange}
                    isInvalid={
                      validated &&
                      (!formData.supervisor?.[0]?._id ||
                        !isValidObjectId(formData.supervisor[0]._id))
                    }
                    style={
                      validated &&
                      (!formData.supervisor?.[0]?._id ||
                        !isValidObjectId(formData.supervisor[0]._id))
                        ? { border: '1px solid red' }
                        : {}
                    }
                    required
                  >
                    <option>Select Supervisor</option>
                    {employees?.data
                      ?.sort((a: Employee, b: Employee) =>
                        a.firstName.localeCompare(b.firstName)
                      )
                      ?.filter((employee: Employee) => employee as Employee)
                      ?.map((user: any) => {
                        const userId = user.id || user._id;
                        return (
                          <option key={userId} value={userId}>
                            {user?.firstName} {user?.lastName}
                          </option>
                        );
                      })}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {validationMessages.supervisor ||
                      'Please select a project lead'}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col sm={6} md={4}>
                <Form.Group controlId="departureDate">
                  <Form.Label>Expected Operator Departure Date</Form.Label>
                  <DatePicker
                    onChange={(value, currentDateString, self) =>
                      handleDateChange(value[0], self._input.name)
                    }
                    name="departureDate"
                    value={formData.departureDate}
                    required
                    render={(_, ref) => {
                      return (
                        <Form.Control
                          type="text"
                          placeholder="Departure Date"
                          ref={ref}
                          name="departureDate"
                          value={formData.departureDate}
                          isInvalid={!!validationMessages.datePicker}
                          style={{ padding: '8px 45px' }}
                        />
                      );
                    }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationMessages.datePicker || 'Please select a date.'}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col sm={6} md={4}>
                <Form.Group controlId="returnDate">
                  <Form.Label>Expected Operator Return Date</Form.Label>
                  <DatePicker
                    onChange={(value, currentDateString, self) =>
                      handleDateChange(value[0], self._input.name)
                    }
                    name="returnDate"
                    value={formData.returnDate}
                    required
                    id="returnDate"
                    render={(_, ref) => {
                      return (
                        <Form.Control
                          type="date"
                          placeholder="Return Date"
                          name="returnDate"
                          ref={ref}
                          id="returnDate"
                          value={formData.returnDate}
                          style={{ padding: '8px 45px' }}
                        />
                      );
                    }}
                  />
                </Form.Group>
              </Col>
              <Col sm={6} md={4}>
                <Form.Group controlId="expectedEquipmentShipDate">
                  <Form.Label>
                    Expected Equipment Ship Date
                    <strong
                      style={{ fontSize: '17px' }}
                      className="text-danger"
                    >
                      *
                    </strong>
                  </Form.Label>
                  <DatePicker
                    onChange={(value, currentDateString, self) =>
                      handleDateChange(value[0], self._input.name)
                    }
                    name="expectedEquipmentShipDate"
                    value={formData.expectedEquipmentShipDate}
                    id="ExpectedEquipmentShipDate"
                    required
                    render={(_, ref) => {
                      return (
                        <Form.Control
                          type="date"
                          placeholder="Expected Equipment Ship Date"
                          ref={ref}
                          id="expectedEquipmentShipDate"
                          name="expectedEquipmentShipDate"
                          value={formData.expectedEquipmentShipDate}
                          required
                          isInvalid={
                            validated && !formData.expectedEquipmentShipDate
                          }
                          style={{ padding: '8px 45px' }}
                        />
                      );
                    }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationMessages.expectedEquipmentShipDate ||
                      'Please enter an Equipment Ship Date.'}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              {/* Customers */}
              <Col md={6} className="">
                <Form.Group controlId="floatingSelectCustomer">
                  <Form.Label>
                    <RequiredLabel>Customer</RequiredLabel>
                  </Form.Label>
                  <div className="dropdown-container" ref={customerRef}>
                    <Form.Control
                      type="text"
                      placeholder="Search company..."
                      value={searchTerm}
                      onChange={handleSearch}
                      onFocus={() => setIsCustomerInputFocused(true)}
                      isInvalid={
                        validated &&
                        (!formData.customer?._id ||
                          !isValidObjectId(formData.customer._id))
                      }
                    />
                    {isSearching && (
                      <Spinner
                        animation="border"
                        size="sm"
                        className="position-absolute"
                        style={{ right: '10px', top: '15px' }}
                      />
                    )}
                    {(isCustomerInputFocused || searchResults?.length > 0) && (
                      <div
                        className="position-absolute w-100 mt-1 bg-white border rounded shadow-sm"
                        style={{ zIndex: 1000 }}
                      >
                        {searchResults?.map(company => (
                          <div
                            key={(company as any).id || company._id}
                            className="px-2 py-1 cursor-pointer hover:bg-gray-100"
                            onClick={async () => {
                              const companyId =
                                (company as any).id || company._id;
                              const selectedCompany = searchResults.find(
                                c => ((c as any).id || c._id) === companyId
                              );
                              setSelectedCustomer(selectedCompany || null);
                              setFormData(prev => {
                                const newFormData = {
                                  ...prev,
                                  customer: { _id: companyId },
                                  title: company.organizationName
                                };
                                if (selectedCompany?.customerRepresentative) {
                                  const repId =
                                    (
                                      selectedCompany.customerRepresentative as any
                                    ).id ||
                                    selectedCompany.customerRepresentative._id;
                                  newFormData.customerRepresentative = {
                                    _id: repId
                                  };
                                } else {
                                  newFormData.customerRepresentative = {
                                    _id: ''
                                  };
                                }
                                return newFormData;
                              });
                              setSearchTerm(company.organizationName);
                              setSearchResults([]);
                              setIsCustomerInputFocused(false);
                              setValidationMessages(prev => ({
                                ...prev,
                                customer: '',
                                title: ''
                              }));

                              // Fetch all customer reps for this company
                              setIsLoadingCustomerReps(true);
                              try {
                                const repsResponse = await GET_COMPANY_REPS(
                                  companyId
                                );
                                console.log(
                                  'All customer reps:',
                                  repsResponse.data
                                );
                                setCustomerReps(repsResponse.data || []);
                              } catch (error) {
                                console.error(
                                  'Error fetching customer reps:',
                                  error
                                );
                                setCustomerReps([]);
                              } finally {
                                setIsLoadingCustomerReps(false);
                              }
                            }}
                          >
                            {company.organizationName}
                          </div>
                        ))}
                        {searchResults?.length > 0 && (
                          <div className="border-top my-2"></div>
                        )}
                        <div
                          className="px-2 py-1 cursor-pointer hover:bg-gray-100 text-primary d-flex align-items-center"
                          onClick={handleShowModal}
                        >
                          <FontAwesomeIcon
                            icon={faCirclePlus}
                            className="me-2"
                          />
                          Add New Company
                        </div>
                      </div>
                    )}
                    <Form.Control.Feedback type="invalid">
                      {validationMessages.customer ||
                        'Please select a customer.'}
                    </Form.Control.Feedback>
                  </div>
                </Form.Group>
              </Col>
              {/* Customer Representative */}
              <Col md={6} className="">
                <Form.Group controlId="floatingSelectCustomerRep">
                  <Form.Label className="mb-2">Customer Rep</Form.Label>
                  <Form.Select
                    name="customerRepresentative"
                    value={formData.customerRepresentative?._id || ''}
                    onChange={handleInputChange}
                    disabled={!selectedCustomer?._id}
                  >
                    <option value="">Select Customer Representative</option>
                    {isLoadingCustomerReps ? (
                      <option value="">Loading...</option>
                    ) : (
                      customerReps.map(rep => {
                        const repId = (rep as any).id || rep._id;
                        return (
                          <option key={repId} value={repId}>
                            {`${rep.firstName || ''} ${rep.lastName || ''}`}
                          </option>
                        );
                      })
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
              {/* Rig */}
              <Col md={6} className="">
                <Form.Group controlId="floatingSelectRig">
                  <Form.Label className="mb-2">Rig</Form.Label>
                  <div className="dropdown-container" ref={rigRef}>
                    <Form.Control
                      type="text"
                      placeholder="Search rig..."
                      value={rigSearchTerm}
                      onChange={handleRigSearch}
                      onFocus={() => setIsRigInputFocused(true)}
                      isInvalid={!!validationMessages.rig}
                    />
                    {isRigSearching && (
                      <Spinner
                        animation="border"
                        size="sm"
                        className="position-absolute"
                        style={{ right: '10px', top: '15px' }}
                      />
                    )}
                    {(isRigInputFocused || rigSearchResults?.length > 0) && (
                      <div
                        className="position-absolute w-100 mt-1 bg-white border rounded shadow-sm"
                        style={{ zIndex: 1000 }}
                      >
                        {rigSearchResults?.length > 0 &&
                          rigSearchResults
                            ?.sort((a, b) => a.name.localeCompare(b.name))
                            ?.map(rig => {
                              const rigId = (rig as any).id || rig._id;
                              return (
                                <div
                                  key={rigId}
                                  className="px-2 py-1 cursor-pointer hover:bg-gray-100"
                                  onClick={() => {
                                    setFormData(prev => ({
                                      ...prev,
                                      rig: { _id: rigId }
                                    }));
                                    setRigSearchTerm(rig.name);
                                    setRigSearchResults([]);
                                    setIsRigInputFocused(false);
                                    setValidationMessages(prev => ({
                                      ...prev,
                                      rig: ''
                                    }));
                                  }}
                                >
                                  {rig.name}
                                </div>
                              );
                            })}
                        {rigSearchResults?.length > 0 && (
                          <div className="border-top my-2"></div>
                        )}
                        <div
                          className="px-2 py-1 cursor-pointer hover:bg-gray-100 text-primary d-flex align-items-center"
                          onClick={handleShowRigModal}
                        >
                          <FontAwesomeIcon
                            icon={faCirclePlus}
                            className="me-2"
                          />
                          Add New Rig
                        </div>
                      </div>
                    )}
                    <Form.Control.Feedback type="invalid">
                      {validationMessages.rig || 'Please select a rig.'}
                    </Form.Control.Feedback>
                  </div>
                </Form.Group>
              </Col>
              {/* {Add Job Type} */}
              <Col md={6} className="">
                <Form.Group controlId="floatingSelectJobType">
                  <Form.Label>
                    <RequiredLabel>Job Type</RequiredLabel>
                  </Form.Label>
                  <div className="dropdown-container" ref={jobTypeRef}>
                    <Form.Control
                      type="text"
                      placeholder="Search job type..."
                      value={jobTypeSearchTerm}
                      onChange={handleJobTypeSearch}
                      onFocus={() => setIsJobTypeInputFocused(true)}
                      isInvalid={!!validationMessages.jobType}
                    />
                    {isJobTypeSearching && (
                      <Spinner
                        animation="border"
                        size="sm"
                        className="position-absolute"
                        style={{ right: '10px', top: '15px' }}
                      />
                    )}
                    {(isJobTypeInputFocused ||
                      jobTypeSearchResults?.length > 0) && (
                      <div
                        className="position-absolute w-100 mt-1 bg-white border rounded shadow-sm"
                        style={{ zIndex: 1000 }}
                      >
                        {jobTypeSearchResults?.length > 0 && (
                          <div className="border-top my-2"></div>
                        )}
                        {jobTypeSearchResults?.map(jobType => {
                          const jobTypeId =
                            (jobType as any).id || jobType._id || '';
                          return (
                            <div
                              key={jobTypeId}
                              className="px-2 py-1 cursor-pointer hover:bg-gray-100"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  jobType: { _id: jobTypeId }
                                }));
                                setJobTypeSearchTerm(jobType.name);
                                setJobTypeSearchResults([]);
                                setIsJobTypeInputFocused(false);
                                setValidationMessages(prev => ({
                                  ...prev,
                                  jobType: ''
                                }));
                              }}
                            >
                              {jobType.name}
                            </div>
                          );
                        })}
                        {jobTypeSearchResults?.length > 0 && (
                          <div className="border-top my-2"></div>
                        )}
                        {/* <div className="px-2 py-1 cursor-pointer hover:bg-gray-100 text-primary d-flex align-items-center">                        <FontAwesomeIcon icon={faCirclePlus} className="me-2" />                        Add New Job Type                      </div> */}
                      </div>
                    )}
                    <Form.Control.Feedback type="invalid">
                      {validationMessages.jobType ||
                        'Please select a Job Type.'}
                    </Form.Control.Feedback>
                  </div>
                </Form.Group>
              </Col>
              {/* {Add job Stage} */}
              <Col md={6} className="">
                <Form.Group controlId="floatingSelectJobStage">
                  <Form.Label>
                    <RequiredLabel>Job Stage</RequiredLabel>
                  </Form.Label>
                  <Form.Select
                    name="jobStage"
                    value={formData.jobStage?._id || ''}
                    onChange={handleInputChange}
                    required
                    isInvalid={!!validationMessages.jobStage}
                  >
                    <option value="">Select Job Stage</option>
                    {sortedJobStages.map((stage: JobStage) => (
                      <option key={stage.id} value={stage.id}>
                        {stage.name.trim()}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {validationMessages.jobStage ||
                      'Please select a Job Stage.'}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              {/* {Add Location} */}
              <Col md={6} className="">
                <Form.Group controlId="floatingSelectLocation">
                  <Form.Label>
                    <RequiredLabel>Location</RequiredLabel>
                  </Form.Label>
                  <div className="dropdown-container" ref={locationRef}>
                    <Form.Control
                      type="text"
                      placeholder="Search location..."
                      value={locationSearchTerm}
                      onChange={handleLocationSearch}
                      onFocus={() => setIsLocationInputFocused(true)}
                      isInvalid={!!validationMessages.location}
                    />
                    {isLocationSearching && (
                      <Spinner
                        animation="border"
                        size="sm"
                        className="position-absolute"
                        style={{ right: '10px', top: '15px' }}
                      />
                    )}
                    {(isLocationInputFocused ||
                      locationSearchResults?.length > 0) && (
                      <div
                        className="position-absolute w-100 mt-1 bg-white border rounded shadow-sm"
                        style={{ zIndex: 1000 }}
                      >
                        {locationSearchResults?.length > 0 &&
                          locationSearchResults?.map(location => {
                            const locationId =
                              (location as any).id || location._id;
                            return (
                              <div
                                key={locationId}
                                className="px-2 py-1 cursor-pointer hover:bg-gray-100"
                                onClick={() => {
                                  setFormData(prev => ({
                                    ...prev,
                                    location: { _id: locationId }
                                  }));
                                  setLocationSearchTerm(location.location);
                                  setLocationSearchResults([]);
                                  setIsLocationInputFocused(false);
                                  setValidationMessages(prev => ({
                                    ...prev,
                                    location: ''
                                  }));
                                }}
                              >
                                {location.location}
                              </div>
                            );
                          })}
                        {locationSearchResults?.length > 0 && (
                          <div className="border-top my-2"></div>
                        )}
                        <div
                          className="px-2 py-1 cursor-pointer hover:bg-gray-100 text-primary d-flex align-items-center"
                          onClick={handleShowLocationModal}
                        >
                          <FontAwesomeIcon
                            icon={faCirclePlus}
                            className="me-2"
                          />
                          Add New Location
                        </div>
                      </div>
                    )}
                    <Form.Control.Feedback type="invalid">
                      {validationMessages.location ||
                        'Please select a Location.'}
                    </Form.Control.Feedback>
                  </div>
                </Form.Group>
              </Col>
              <Col md={6} className="">
                <Form.Group controlId="floatingInputLeavingFrom">
                  <Form.Label className="mb-2">Leaving From</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Leaving From"
                    name="leavingFrom"
                    value={formData.leavingFrom}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6} className="">
                <Form.Group controlId="floatingInputJobNumber">
                  <Form.Label>
                    <RequiredLabel>Job Number</RequiredLabel>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="001JBS-OPS"
                    name="jobNumber"
                    value={formData.jobNumber}
                    required
                    isInvalid={validated && !formData.jobNumber}
                    onChange={handleInputChange}
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationMessages.jobNumber || 'Job Number is required'}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col xs={12} className="">
                <Form.Group controlId="floatingProjectDescription">
                  <Form.Label>Job Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    placeholder="Enter job description here"
                    style={{ height: '100px' }}
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} className="">
                <Form.Group controlId="floatingProjectComments">
                  <Form.Label>Job Comments</Form.Label>
                  <Form.Control
                    as="textarea"
                    placeholder="Enter job comments here"
                    style={{ height: '100px' }}
                    name="comment"
                    value={formData.comment}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} className="">
                <div
                  style={
                    isLoading
                      ? {
                          transition: 'filter 0.5s ease',
                          filter: 'blur(5px);'
                        }
                      : undefined
                  }
                >
                  <div className="d-flex justify-content-end gap-3 my-5">
                    <Link to="/home">
                      <Button
                        disabled={isLoading}
                        variant="phoenix-primary"
                        className="px-5"
                      >
                        Cancel
                      </Button>
                    </Link>
                    <Button
                      variant="primary"
                      className="px-5 px-sm-15"
                      onClick={handleCreateJob}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Creating...' : 'Create Job'}
                    </Button>
                  </div>
                </div>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
      <Modal
        show={showCreateModal}
        onHide={handleCloseModal}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Create New Company</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CreateCustomer isModal={true} onSuccess={handleCustomerCreated} />
        </Modal.Body>
      </Modal>

      <RigModal
        show={showRigModal}
        handleClose={handleCloseRigModal}
        onSuccess={handleRigCreated}
      />

      <LocationModal
        show={showLocationModal}
        handleClose={handleCloseLocationModal}
        onSuccess={handleLocationCreated}
      />
    </div>
  );
};

export default memo(CreateNewProject);
